import { useState } from 'react';
import { Box, Button, IconButton, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { FOutlinedValue } from '../../../../../../components/baseV2';
import { COLLAPSED_EVENT_COUNT } from './constants';
import { RateEventField } from './components';
import {
  DURATION_TRACKER_V2_ADD_BUTTON_ROW_SX,
  DURATION_TRACKER_V2_ADD_BUTTON_SX,
  DURATION_TRACKER_V2_CONTAINER_SX,
  DURATION_TRACKER_V2_LINK_SX,
  DURATION_TRACKER_V2_TITLE_SX,
  RATE_TRACKER_V2_EVENTS_GRID_SX,
  RATE_TRACKER_V2_TITLE_ROW_SX,
  RATE_TRACKER_V2_TOP_ROW_SX,
  RATE_TRACKER_V2_TOTAL_FIELD_SX,
} from './styles';

import type { RateTimeTrackerProps } from './types';

function RateTimeTracker({
  isEditable,
  savingInProgress,
  dateStart,
  expectedDuration,
  rateEvents,
  onAddRateEvent,
  onDeleteRateEvent,
  onUpdateRateEvent,
  onRateTutorialOpen,
}: RateTimeTrackerProps) {
  const { t } = useTranslation('sessionNotes');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAllV2Events, setShowAllV2Events] = useState(false);
  const sessionStart = dateStart ? moment(dateStart) : null;
  const sessionEnd =
    sessionStart && expectedDuration != null
      ? sessionStart.clone().add(expectedDuration, 'seconds')
      : null;
  const rateEventsToDisplay =
    isMobile && !showAllV2Events && rateEvents.length > COLLAPSED_EVENT_COUNT
      ? rateEvents.slice(0, COLLAPSED_EVENT_COUNT)
      : rateEvents;

  const handleToggleRateEvents = () => {
    setShowAllV2Events((prev) => !prev);
  };

  return (
    <Box data-testid="session-rate-tracker-container" sx={DURATION_TRACKER_V2_CONTAINER_SX}>
      <Box sx={RATE_TRACKER_V2_TOP_ROW_SX}>
        <Box sx={RATE_TRACKER_V2_TITLE_ROW_SX}>
          <Typography sx={DURATION_TRACKER_V2_TITLE_SX}>
            {t('goalsPracticed.rateTracker')}
          </Typography>
          <IconButton size="small" onClick={onRateTutorialOpen}>
            <InfoOutlinedIcon sx={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>
        <FOutlinedValue
          label={t('goalsPracticed.totalEvents')}
          value={`${rateEvents.length} ${t('goalsPracticed.totalEvents').toLowerCase()}`}
          sx={RATE_TRACKER_V2_TOTAL_FIELD_SX}
        />
      </Box>
      <Box sx={RATE_TRACKER_V2_EVENTS_GRID_SX}>
        <RateEventField
          label={t('goalsPracticed.sessionStart')}
          time={sessionStart}
          minTime={sessionStart}
          maxTime={sessionEnd}
          disabled
        />
        {rateEventsToDisplay.map((rateEvent, index) => (
          <RateEventField
            key={rateEvent.id ?? `rate-event-${index}`}
            label={`${t('goalsPracticed.event')} ${index + 1}`}
            time={rateEvent.date_of_event ? moment(rateEvent.date_of_event) : null}
            minTime={sessionStart}
            maxTime={sessionEnd}
            disabled={!isEditable || savingInProgress}
            editable={isEditable}
            onDelete={() => onDeleteRateEvent(rateEvent)}
            onUpdate={(time) => onUpdateRateEvent(rateEvent, time)}
          />
        ))}
        <RateEventField
          label={t('goalsPracticed.sessionEnd')}
          time={sessionEnd}
          minTime={sessionStart}
          maxTime={sessionEnd}
          disabled
        />
      </Box>
      {isMobile && rateEvents.length > COLLAPSED_EVENT_COUNT && (
        <Link onClick={handleToggleRateEvents} sx={DURATION_TRACKER_V2_LINK_SX}>
          {showAllV2Events ? t('goalsPracticed.seeLess') : t('goalsPracticed.seeMore')}
        </Link>
      )}
      {isEditable && (
        <Box sx={DURATION_TRACKER_V2_ADD_BUTTON_ROW_SX}>
          <Button
            sx={DURATION_TRACKER_V2_ADD_BUTTON_SX}
            disabled={
              rateEvents.some((event) => !event.date_of_event) ||
              !isEditable ||
              savingInProgress
            }
            onClick={onAddRateEvent}
          >
            {t('goalsPracticed.addEvent')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default RateTimeTracker;
